import React, { useState } from 'react';
import { useUserProfile } from '../../helper/UserProfile';
import { useRevalidationData } from '../../helper/UserRevalidationProgress';
import '../../stylesheets/revalidation.scss';
import supabase from '../../helper/supabaseClient';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { faPenToSquare } from '@fortawesome/free-solid-svg-icons';
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { faFile } from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';
import { downloadCPDExcelDirect } from '../../helper/CPDExcelDownload';
import MobileRevalNav from '../mobile/MobileRevalNav';

function CPD() {
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [date, setDate] = useState("");
    const [hours, setHours] = useState("");
    const [participate, setParticipate] = useState("");
    const [method, setMethod] = useState("");
    const [topic, setTopic] = useState("");
    const [code, setCode] = useState("");
    const [standards, setStandards] = useState("");
    const [isEditing, setIsEditing ] = useState(false);
    const [editId, setEditId] = useState("");
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [file, setFile] = useState("");
    const [filename, setFilename] = useState("");

    const { profile, loading } = useUserProfile();

    const { totalHours, totalParticipate, cpd, refresh } = useRevalidationData();   

    //Code Dropdown
    const options = [
        {value: 'Prioritise people', label: 'Prioritise people'},
        {value: 'Practise effectively', label: 'Practise effectively'},
        {value: 'Preserve safety', label: 'Preserve safety'},
        {value: 'Promote professionalism and trust', label: 'Promote professionalism and trust'}
    ];

    const handleChange = (selected) => {
        setSelectedOptions(selected);
    }

    //Submit button
    const handleSubmit = async (e) => {
    e.preventDefault();

    if (!profile?.id) {
        setError("User not loaded. Please wait and try again.");
        return;
    }

    if (isEditing && editId) {
    const { error } = await supabase
      .from('cpd_entries')
      .update({
        date,
        method,
        topic,
        linktocode: selectedOptions.map(option => option.value).join(', '),
        linktostandards: standards,
        hours,
        participatoryhours: participate,
      })
      .eq('id', editId);

    if (error) {
      setError("Could not update entry");
      return;
    }
    setSuccess("Entry updated");
    setIsEditing(false);
    setEditId(null);
    refresh();
    } else {

        let filePath = null;

        if (file) {
            // Create a unique file path: userId/timestamp_filename.ext
            filePath = `${profile.id}/${file.name}`;

            const { error: uploadError } = await supabase.storage
            .from('cpd-evidence')
            .upload(filePath, file, {
                cacheControl: '3600',
                upsert: true,
            });

            if (uploadError) {
            setError("Failed to upload evidence file.");
            return; // stop submission if upload failed
            }

            const { dbError } = await supabase
            .from('revalidation_documents')
            .insert({
                user_id: profile.id,
                document_type: "CPD Evidence",
                file_path: filePath,
                upload_date: new Date(),
                bucket: "cpd-evidence"
            });

            if (dbError) {
                setError("File uploaded but database update failed.");
            }
        }

        const { data, error } = await supabase
            .from('cpd_entries')
            .insert({
            date,
            method,
            topic,
            linktocode: selectedOptions.map(option => option.value).join(', '),
            linktostandards: standards,
            hours,
            participatoryhours: participate,
            user_id: profile.id,
            file_path: filePath,
            });

        if (error) {
            setError("There has been an issue saving the entry.");
            return;
        }

        setSuccess("Your log has been updated");
        setError("");
        refresh();
        console.log({error})
        }

    // Reset form
    setError("");
    setDate("");
    setHours("");
    setParticipate("");
    setMethod("");
    setTopic("");
    setSelectedOptions([]);
    setStandards("");
    setFile(null);
    setFilename("");
    return;
    };

    //Delete function
    const handleDelete = async (id) => {
        const { data: entryData, error: fetchError } = await supabase
        .from('cpd_entries')
        .select('file_path')
        .eq('id', id)
        .single();

        if (fetchError) {
            setError("Could not find entry to delete");
            return;
        }

        if (entryData?.file_path) {
            // Delete file from storage bucket
            const { error: deleteFileError } = await supabase.storage
            .from('cpd-evidence')
            .remove([entryData.file_path]);

            if (deleteFileError) {
            setError("Failed to delete evidence file");
            return;
            }
        }

        // Now delete the DB entry
        const { error } = await supabase
            .from('cpd_entries')
            .delete()
            .eq('id', id);

        if (error) {
            console.error(error);
            setError("Could not delete entry");
        }
        
        const { dbError} = await supabase
        .from('revalidation_documents')
        .delete()
        .eq("file_path", [entryData.file_path]);
        
        if (dbError) {
            setError("File deleted but database update failed.")
        } else {
            setSuccess("Entry deleted");
            setError("");
            refresh();
        }
    };
    
    const populateFormForEdit = (entry) => {
        setDate(entry.date);
        setHours(entry.hours);
        setParticipate(entry.participatoryhours);
        setMethod(entry.method);
        setTopic(entry.topic);
        setCode(entry.code);
        setStandards(entry.linktostandards);
        setEditId(entry.id);
        setIsEditing(true);

         if (entry.linktocode) {
        const codeArray = entry.linktocode.split(',').map(code => code.trim());
        const selected = options.filter(opt => codeArray.includes(opt.value));
        setSelectedOptions(selected);
    } else {
        setSelectedOptions([]);
    }
    };

  return (
    <div>
        <div className="mob-only">
            <MobileRevalNav />
        </div>
        <div className="dashboard-body">
            <h1>CONTINUING PROFESSIONAL DEVELOPMENT</h1>
            <p>Please fill in the form for each learning activity. You must reach 35 hours of CPD, of which 20 hours must be participatory.</p>

            {/*Desktop*/}
            <form onSubmit = { handleSubmit } className="desktop-only">
                <table className="cpd reval-input">
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Number of hours</th>
                            <th>Number of participatory hours</th>
                            <th>Evidence Upload (optional)</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><input type="date" value={date} onChange={(e) =>setDate(e.target.value)}></input></td>
                            <td><input type="text" value={hours}onChange={(e) => setHours(e.target.value)}></input></td>
                            <td><input type="text" value={participate} onChange={(e) => setParticipate(e.target.value)}></input></td>
                            <td><input type="file" onChange={(e) => {
                                if (e.target.files.length > 0) {
                                        setFile(e.target.files[0]);
                                        setFilename(e.target.files[0].name);
                                } else {
                                    setFile(null);
                                    setFilename("");
                                }
                                }}></input></td>
                        </tr>
                    </tbody>
                </table>
                <br></br>
                <table className="cpd reval-input">
                    <thead>
                        <tr>
                            <th>Method</th>
                            <th>Topic(s)</th>
                            <th>Link to Code <a href="https://www.nmc.org.uk/globalassets/sitedocuments/nmc-publications/nmc-code.pdf" target="_blank"><FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{paddingLeft:"2px"}} /></a></th>
                            <th>Link to Standards <br></br>of Proficiency  <a href="https://www.nmc.org.uk/globalassets/sitedocuments/nmc-publications/nmc-code.pdf" target="_blank"><FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{paddingLeft:"2px"}} /></a></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><textarea value={method} onChange={(e) => setMethod(e.target.value)}></textarea></td>
                            <td><textarea value={topic} onChange={(e) => setTopic(e.target.value)}></textarea></td>
                            <td style={{verticalAlign:"top"}}><Select
                                    isMulti
                                    options={options}
                                    value={selectedOptions}
                                    onChange={handleChange} 
                                    className="reval-dropdown"
                                    classNamePrefix="rs"/></td>
                            <td><textarea value={standards} onChange={(e) => setStandards(e.target.value)}></textarea></td>
                        </tr>
                    </tbody>
                </table>
                <br></br>
                <button className="reval" type="submit" disabled={!profile?.id || loading}>Submit</button>
                {error && <p className="error-message"><br></br>{error}</p>}
                {success && <p className="success"><br></br>{success}</p>}
            </form>

            {/*Mobile*/}
            <form onSubmit = { handleSubmit } className="mob-only">
                <table className="cpd reval-input">
                    <tbody>
                        <tr>
                            <th>Date</th>
                            <td><input type="date" value={date} onChange={(e) =>setDate(e.target.value)}></input></td>
                        </tr>
                        <tr>
                            <th>Number of hours</th>
                            <td><input type="text" value={hours}onChange={(e) => setHours(e.target.value)}></input></td>
                        </tr>
                        <tr>
                            <th>Number of participatory hours</th>
                            <td><input type="text" value={participate} onChange={(e) => setParticipate(e.target.value)}></input></td>
                        </tr>
                        <tr>
                            <th>Method</th>
                            <td><textarea value={method} onChange={(e) => setMethod(e.target.value)}></textarea></td>
                        </tr>
                        <tr>
                            <th>Topic(s)</th>
                            <td><textarea value={topic} onChange={(e) => setTopic(e.target.value)}></textarea></td>
                        </tr>
                        <tr>
                            <th>Link to Code <a href="https://www.nmc.org.uk/globalassets/sitedocuments/nmc-publications/nmc-code.pdf" target="_blank"><FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{paddingLeft:"2px"}} /></a></th>
                            <td style={{verticalAlign:"top"}}><Select
                                    isMulti
                                    options={options}
                                    value={selectedOptions}
                                    onChange={handleChange} 
                                    className="reval-dropdown"
                                    classNamePrefix="rs"/></td>
                        </tr>
                        <tr>
                            <th>Link to Standards <br></br>of Proficiency  <a href="https://www.nmc.org.uk/globalassets/sitedocuments/standards/2024/standards-of-proficiency-for-nurses.pdf" target="_blank"><FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{paddingLeft:"2px"}} /></a></th>
                            <td><textarea value={standards} onChange={(e) => setStandards(e.target.value)}></textarea></td>
                        </tr>
                        <tr>
                             <th>Evidence Upload (optional)</th>
                            <td><input type="file" onChange={(e) => {  
                                if (e.target.files.length > 0) {
                                        setFile(e.target.files[0]);
                                        setFilename(e.target.files[0].name);
                                } else {
                                    setFile(null);
                                    setFilename("");
                                }
                                }}></input></td>
                        </tr>
                    </tbody>
                </table>
                <br></br>
                {isEditing === false ? <button className="reval" type="submit" disabled={!profile?.id || loading}>Submit</button> : <button className="reval" type="submit">Update</button>}
                {error && <p className="error-message"><br></br>{error}</p>}
                {success && <p className="success"><br></br>{success}</p>}
            </form>

            <br></br>
            <h3>YOUR EXISTING LOG</h3>
            <p>{totalHours === 0 ? "Your log is empty. Please log your hours above." : `You have currently logged ${totalHours} hours, out of which ${totalParticipate} are participatory (P).`}</p>
            <table className="cpd reval reval-log desktop-only">
                <thead>
                    <tr>
                        <th>Date</th>
                        <th style={{width:"17%"}}>Method</th>
                        <th style={{width:"17%"}}>Topic</th>
                        <th>Link to Code</th>
                        <th>Link to Standards of Proficiency</th>
                        <th>Number of Hours (P)</th>
                        <th style={{width: "80px"}}>Evidence Uploaded?</th>
                        <th style={{width:"1rem", backgroundColor:"#fff"}}></th>
                        <th style={{width:"1rem", backgroundColor:"#fff"}}></th>
                    </tr>
                </thead>
                <tbody>
            {cpd.map((entry, index) => (
                <tr key={index}>
                <td>{entry.date}</td>
                <td>{entry.method}</td>
                <td>{entry.topic}</td>
                <td>{entry.linktocode}</td>
                <td>{entry.linktostandards}</td>
                <td>{entry.hours} ({entry.participatoryhours})</td>
                <td style={{textAlign: "center"}}>{entry.file_path ? <a href={`https://dyuqmgoqgozadkxqnqqq.supabase.co/storage/v1/object/public/cpd-evidence/${entry.file_path}`} target="_blank"><FontAwesomeIcon icon={faFile} /></a> : ""}</td>
                <td><FontAwesomeIcon icon={faPenToSquare} style={{fontSize:"0.8rem"}} onClick={() => populateFormForEdit(entry)}/></td>
                <td><FontAwesomeIcon icon={faTrashCan} style={{fontSize:"0.8rem"}} onClick={() => handleDelete(entry.id)}/></td>
                </tr>
            ))}
            </tbody>
            </table>

            <table className="cpd reval reval-log mob-only">
                <tr>
                    <th>Date</th>
                    <th>Method</th>
                    <th>Topic</th>
                    <th>Hours (P)</th>
                    <th style={{width:"1rem", backgroundColor:"#fff"}}></th>
                    <th style={{width:"1rem", backgroundColor:"#fff"}}></th>
                </tr>
                {cpd.map((entry, index) => (
                <tr key={index}>
                <td>{entry.date}</td>
                <td>{entry.method}</td>
                <td>{entry.topic}</td>
                <td>{entry.hours} ({entry.participatoryhours})</td>
                <td><FontAwesomeIcon icon={faPenToSquare} onClick={() => populateFormForEdit(entry)}/></td>
                <td><FontAwesomeIcon icon={faTrashCan} onClick={() => handleDelete(entry.id)}/></td>
                </tr>
            ))}
            </table>
            <br></br>
            <p className="mob-only">For best experience, please use desktop app.</p>
            <button className="reval" onClick={() => downloadCPDExcelDirect(cpd, profile)}>Download CPD Log</button>
        </div>
        <div className="mob-bottom-buffer">
        </div>
    </div>
  )
}

export default CPD